import React, { useState, useEffect } from 'react';
import { SponsorService, SponseeProgress } from '../services/sponsorService';
import styles from '../styles/sponseeDashboard.module.css';

interface SponseeDashboardProps {
  isOpen: boolean;
  onClose: () => void;
  shareToken: string;
  sponseeName: string;
}

const SponseeDashboard: React.FC<SponseeDashboardProps> = ({
  isOpen,
  onClose,
  shareToken,
  sponseeName
}) => {
  const [progress, setProgress] = useState<SponseeProgress | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && shareToken) {
      loadProgress();
    }
  }, [isOpen, shareToken]);

  const loadProgress = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await SponsorService.getSponseeProgress(shareToken);
      setProgress(data);
    } catch (err: any) {
      setError('Failed to load sponsee progress. They may have disabled sharing.');
      console.error('Error loading sponsee progress:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStepStatus = (stepKey: string): string => {
    if (!progress) return 'not_started';
    const stepData = progress.progress.steps[stepKey];
    return stepData?.status || 'not_started';
  };

  const getCompletedStepsCount = (): number => {
    if (!progress) return 0;
    return Object.values(progress.progress.steps).filter(
      (step: any) => step.status === 'completed'
    ).length;
  };

  const getInProgressStepsCount = (): number => {
    if (!progress) return 0;
    return Object.values(progress.progress.steps).filter(
      (step: any) => step.status === 'in_progress'
    ).length;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyDown}>
        <div className={styles.modalHeader}>
          <div>
            <h2 className={styles.modalTitle}>{sponseeName}'s Progress</h2>
            <p className={styles.subtitle}>Read-only view</p>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          {loading && (
            <div className={styles.loading}>Loading progress...</div>
          )}

          {error && (
            <div className={styles.error}>{error}</div>
          )}

          {!loading && progress && (
            <>
              {/* Summary Section */}
              <div className={styles.summarySection}>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryLabel}>Steps Completed</div>
                  <div className={styles.summaryValue}>{getCompletedStepsCount()} / 12</div>
                </div>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryLabel}>In Progress</div>
                  <div className={styles.summaryValue}>{getInProgressStepsCount()}</div>
                </div>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryLabel}>Resentments</div>
                  <div className={styles.summaryValue}>{progress.progress.inventories.resentments}</div>
                </div>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryLabel}>Fears</div>
                  <div className={styles.summaryValue}>{progress.progress.inventories.fears}</div>
                </div>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryLabel}>Harms</div>
                  <div className={styles.summaryValue}>{progress.progress.inventories.harms}</div>
                </div>
              </div>

              {/* Twelve Steps Progress */}
              <div className={styles.stepsSection}>
                <h3 className={styles.sectionTitle}>Twelve Steps Progress</h3>
                <div className={styles.stepsGrid}>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(stepNum => {
                    const stepKey = `step_${stepNum}`;
                    const status = getStepStatus(stepKey);
                    const statusIcons = {
                      completed: '✓',
                      in_progress: '◐',
                      not_started: '○'
                    };
                    const statusLabels = {
                      completed: 'Completed',
                      in_progress: 'In Progress',
                      not_started: 'Not Started'
                    };
                    
                    return (
                      <div key={stepNum} className={`${styles.stepCard} ${styles[status]}`}>
                        <div className={styles.stepNumber}>Step {stepNum}</div>
                        <div className={styles.stepStatus}>
                          <span className={styles.statusIcon}>{statusIcons[status as keyof typeof statusIcons] || '○'}</span>
                          <span className={styles.statusLabel}>{statusLabels[status as keyof typeof statusLabels] || 'Not Started'}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button onClick={onClose} className={styles.closeFooterButton}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SponseeDashboard;

